import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Paper, Grid, Box, Typography, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination, TableSortLabel,
  Chip, IconButton, Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Link
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { KeyboardDatePicker } from '@material-ui/pickers'
import SyncIcon from '@material-ui/icons/Sync'
import GetAppIcon from '@material-ui/icons/GetApp'
import EventIcon from '@material-ui/icons/Event'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import VisibilityIcon from '@material-ui/icons/Visibility'
import WarningIcon from '@material-ui/icons/Warning'

const statusColors = {
  overdue: '#f44336',
  never_done: '#e91e63',
  needs_completion: '#9c27b0',
  due_soon: '#ff9800',
  ok: '#4caf50',
  ignored: '#9e9e9e'
}

const statusLabels = {
  overdue: 'Overdue',
  never_done: 'Never Done',
  needs_completion: 'Needs Completion',
  due_soon: 'Due Soon',
  ok: 'OK',
  ignored: 'Ignored'
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3)
  },
  statsCard: {
    padding: theme.spacing(2),
    textAlign: 'center',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'box-shadow 0.2s, transform 0.1s',
    '&:hover': {
      boxShadow: theme.shadows[4],
      transform: 'translateY(-1px)'
    }
  },
  statsCardActive: {
    boxShadow: theme.shadows[6],
    outline: '2px solid',
    outlineOffset: -2
  },
  statsNumber: {
    fontSize: '2rem',
    fontWeight: 'bold'
  },
  filterBar: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  tableContainer: {
    marginTop: theme.spacing(2)
  },
  chip: {
    fontWeight: 'bold'
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(1)
  }
}))

const HerbicideScheduleDashboard = (props) => {
  const classes = useStyles()
  const { url } = props

  // Data state
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [stats, setStats] = useState({ overdue: 0, never_done: 0, needs_completion: 0, due_soon: 0, ok: 0, ignored: 0, total: 0 })

  // Filter/pagination/sort state
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('nextScheduledDate')
  const [sortOrder, setSortOrder] = useState('asc')

  // Dialog state
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false)
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [overrideDate, setOverrideDate] = useState(null)
  const [overrideReason, setOverrideReason] = useState('')
  const [completeDate, setCompleteDate] = useState(null)
  const [completeNotes, setCompleteNotes] = useState('')
  const [ignoreDialogOpen, setIgnoreDialogOpen] = useState(false)
  const [ignoreReason, setIgnoreReason] = useState('')

  const fetchItems = useCallback(async () => {
    setLoading(true)
    try {
      const params = { page, limit: rowsPerPage, sortBy, sortOrder }
      if (statusFilter !== 'all') params.status = statusFilter
      if (searchTerm) params.search = searchTerm
      const resp = await axios.get(`${url}/api/herbicide-schedule`, { params })
      setItems(resp.data.items || [])
      setTotalCount(resp.data.total || 0)
    } catch (err) {
      console.error('Error fetching schedule items:', err)
    }
    setLoading(false)
  }, [url, page, rowsPerPage, statusFilter, searchTerm, sortBy, sortOrder])

  const fetchStats = useCallback(async () => {
    try {
      const resp = await axios.get(`${url}/api/herbicide-schedule/stats`)
      setStats(resp.data)
    } catch (err) {
      console.error('Error fetching stats:', err)
    }
  }, [url])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const handleSync = async () => {
    setSyncing(true)
    try {
      await axios.post(`${url}/api/herbicide-schedule/sync`)
      await Promise.all([fetchItems(), fetchStats()])
    } catch (err) {
      console.error('Error syncing:', err)
    }
    setSyncing(false)
  }

  const handleExport = async () => {
    try {
      const resp = await axios.get(`${url}/api/herbicide-schedule/export`, { responseType: 'blob' })
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data]))
      const link = document.createElement('a')
      link.href = blobUrl
      link.setAttribute('download', `herbicide_schedule_${new Date().toISOString().slice(0, 10)}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
    } catch (err) {
      console.error('Error exporting:', err)
    }
  }

  // Override dialog
  const openOverrideDialog = (item) => {
    setSelectedItem(item)
    setOverrideDate(item.overriddenNextDate ? new Date(item.overriddenNextDate) : (item.nextScheduledDate ? new Date(item.nextScheduledDate) : new Date()))
    setOverrideReason('')
    setOverrideDialogOpen(true)
  }

  const handleOverrideSubmit = async () => {
    if (!selectedItem || !overrideDate) return
    try {
      await axios.put(`${url}/api/herbicide-schedule/${selectedItem._id}/override`, {
        date: overrideDate,
        reason: overrideReason
      })
      setOverrideDialogOpen(false)
      await Promise.all([fetchItems(), fetchStats()])
    } catch (err) {
      console.error('Error overriding date:', err)
    }
  }

  // Complete dialog
  const openCompleteDialog = (item) => {
    setSelectedItem(item)
    setCompleteDate(new Date())
    setCompleteNotes('')
    setCompleteDialogOpen(true)
  }

  const handleCompleteSubmit = async () => {
    if (!selectedItem || !completeDate) return
    try {
      await axios.put(`${url}/api/herbicide-schedule/${selectedItem._id}/complete`, {
        completedDate: completeDate,
        notes: completeNotes
      })
      setCompleteDialogOpen(false)
      await Promise.all([fetchItems(), fetchStats()])
    } catch (err) {
      console.error('Error marking complete:', err)
    }
  }

  // Ignore dialog
  const openIgnoreDialog = (item) => {
    setSelectedItem(item)
    setIgnoreReason('')
    setIgnoreDialogOpen(true)
  }

  const handleIgnoreSubmit = async () => {
    if (!selectedItem) return
    try {
      await axios.put(`${url}/api/herbicide-schedule/${selectedItem._id}/ignore`, {
        reason: selectedItem.ignored ? '' : (ignoreReason || 'Manually ignored')
      })
      setIgnoreDialogOpen(false)
      await Promise.all([fetchItems(), fetchStats()])
    } catch (err) {
      console.error('Error toggling ignore:', err)
    }
  }

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('asc')
    }
    setPage(0)
  }

  const handleCardClick = (status) => {
    if (statusFilter === status) {
      setStatusFilter('all')
    } else {
      setStatusFilter(status)
    }
    setPage(0)
  }

  const formatDate = (d) => {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
  }

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <Typography variant="h5">Herbicide Application Schedule</Typography>
        <div className={classes.buttonGroup}>
          <Button
            variant="contained"
            color="primary"
            startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync from Aspire'}
          </Button>
          <Button variant="outlined" startIcon={<GetAppIcon />} onClick={handleExport}>
            Export Excel
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        {[
          { key: 'overdue', label: 'Overdue', color: statusColors.overdue, count: stats.overdue },
          { key: 'never_done', label: 'Never Done', color: statusColors.never_done, count: stats.never_done },
          { key: 'needs_completion', label: 'Needs Completion', color: statusColors.needs_completion, count: stats.needs_completion },
          { key: 'due_soon', label: 'Due Soon', color: statusColors.due_soon, count: stats.due_soon },
          { key: 'ok', label: 'OK', color: statusColors.ok, count: stats.ok },
          { key: 'ignored', label: 'Ignored', color: statusColors.ignored, count: stats.ignored },
          { key: 'all', label: 'Total', color: '#2196f3', count: stats.total }
        ].map((card) => (
          <Grid item xs={6} md key={card.key}>
            <Paper
              className={`${classes.statsCard} ${statusFilter === card.key ? classes.statsCardActive : ''}`}
              style={{ borderLeft: `4px solid ${card.color}`, outlineColor: statusFilter === card.key ? card.color : undefined }}
              onClick={() => handleCardClick(card.key)}
            >
              <Typography className={classes.statsNumber} style={{ color: card.color }}>{card.count}</Typography>
              <Typography variant="body2" color="textSecondary">{card.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filter Bar */}
      <Paper className={classes.filterBar}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0) }}
            label="Status"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="overdue">Overdue</MenuItem>
            <MenuItem value="never_done">Never Done</MenuItem>
            <MenuItem value="needs_completion">Needs Completion</MenuItem>
            <MenuItem value="due_soon">Due Soon</MenuItem>
            <MenuItem value="ok">OK</MenuItem>
            <MenuItem value="ignored">Ignored</MenuItem>
          </Select>
        </FormControl>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search site name, address, building number..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }}
          style={{ minWidth: 300 }}
        />
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    { id: 'siteName', label: 'Site Name' },
                    { id: 'communityName', label: 'Community Name/#' },
                    { id: 'address', label: 'Address' },
                    { id: 'buildingNumber', label: 'Building Number' },
                    { id: 'aspirePropertyId', label: 'Aspire ID' },
                    { id: 'portalLastAppDate', label: 'Portal Date' },
                    { id: 'aspireTicketDate', label: 'Aspire Date' },
                    { id: 'lastAppliedDate', label: 'Effective Last App' },
                    { id: 'nextScheduledDate', label: 'Next Scheduled' },
                    { id: 'scheduledTicketDate', label: 'Scheduled Ticket' },
                    { id: 'daysUntilNext', label: 'Days Until' },
                    { id: 'status', label: 'Status' }
                  ].map((col) => (
                    <TableCell key={col.id} sortDirection={sortBy === col.id ? sortOrder : false}>
                      <TableSortLabel
                        active={sortBy === col.id}
                        direction={sortBy === col.id ? sortOrder : 'asc'}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      <Typography color="textSecondary" style={{ padding: 20 }}>
                        No schedule items found. Click "Sync from Aspire" to populate.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => {
                    const effectiveNext = item.overriddenNextDate || item.nextScheduledDate
                    const tickets = item.pendingTickets || []
                    const now = new Date()

                    // Most recent non-complete ticket with actual hours
                    const recentWorked = tickets
                      .filter(t => t.statusId !== 7 && t.hoursActual > 0)
                      .sort((a, b) => new Date(b.scheduledDate || 0) - new Date(a.scheduledDate || 0))[0] || null

                    // Next future scheduled ticket (open/scheduled, not complete)
                    const futureTicket = tickets
                      .filter(t => t.statusId !== 7 && t.scheduledDate && new Date(t.scheduledDate) >= now)
                      .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))[0] || null

                    // For the Scheduled Ticket column — find the ticket matching scheduledTicketDate
                    const scheduledColTicket = tickets
                      .find(t => t.scheduledDate && item.scheduledTicketDate &&
                        new Date(t.scheduledDate).getTime() === new Date(item.scheduledTicketDate).getTime()) || null

                    return (
                      <TableRow key={item._id} style={item.ignored ? { opacity: 0.5 } : {}}>
                        <TableCell>
                          {item.aspirePropertyId ? (
                            <Link
                              href={`${process.env.REACT_APP_ASPIRE_BASE_URL}/app/properties/overview/${item.aspirePropertyId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontWeight: 500 }}
                            >
                              {item.siteName || '(unnamed)'}
                            </Link>
                          ) : (
                            item.siteName
                          )}
                        </TableCell>
                        <TableCell>{item.communityName || ''}</TableCell>
                        <TableCell>
                          {item.jobsite ? (
                            <Link href={`/clientmapview/${item.jobsite}`} style={{ fontWeight: 500 }}>
                              {item.address || '—'}
                            </Link>
                          ) : (
                            item.address || '—'
                          )}
                        </TableCell>
                        <TableCell>{item.buildingNumber || ''}</TableCell>
                        <TableCell>{item.aspirePropertyId || '—'}</TableCell>
                        <TableCell>{formatDate(item.portalLastAppDate)}</TableCell>
                        <TableCell>{formatDate(item.aspireTicketDate)}</TableCell>
                        <TableCell>{formatDate(item.lastAppliedDate)}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                            <span>{formatDate(effectiveNext)}</span>
                            {item.aspireMismatch && (
                              <Tooltip title={item.scheduledTicketDate ? 'Aspire ticket date does not match — update needed' : 'No Aspire ticket scheduled — needs to be created'}>
                                <WarningIcon style={{ color: '#ff9800', fontSize: 16 }} />
                              </Tooltip>
                            )}
                          </Box>
                          {item.overriddenNextDate && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              (overridden)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                            {scheduledColTicket ? (
                              <Link
                                href={`${process.env.REACT_APP_ASPIRE_BASE_URL}/app/worktickets/details/${scheduledColTicket.workTicketId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {formatDate(item.scheduledTicketDate)}
                              </Link>
                            ) : (
                              <span>{formatDate(item.scheduledTicketDate)}</span>
                            )}
                            {item.aspireMismatch && (
                              <Tooltip title={item.scheduledTicketDate ? 'Does not match portal override date' : 'No ticket in Aspire'}>
                                <WarningIcon style={{ color: '#ff9800', fontSize: 16 }} />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography style={{ color: statusColors[item.status] || '#333', fontWeight: 'bold' }}>
                            {item.daysUntilNext != null ? item.daysUntilNext : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabels[item.status] || item.status}
                            size="small"
                            className={classes.chip}
                            style={{
                              backgroundColor: statusColors[item.status] || '#ccc',
                              color: '#fff'
                            }}
                          />
                          {recentWorked && (
                            <Typography variant="caption" display="block" style={{ color: '#ff9800', lineHeight: 1.3, marginTop: 4 }}>
                              <Link
                                href={`${process.env.REACT_APP_ASPIRE_BASE_URL}/app/worktickets/details/${recentWorked.workTicketId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'inherit' }}
                              >
                                WT#{recentWorked.workTicketId}
                              </Link>
                              {' — '}{recentWorked.status} ({recentWorked.hoursActual}h actual)
                            </Typography>
                          )}
                          {futureTicket && futureTicket !== recentWorked && (
                            <Typography variant="caption" display="block" style={{ color: '#2196f3', lineHeight: 1.3, marginTop: 2 }}>
                              <Link
                                href={`${process.env.REACT_APP_ASPIRE_BASE_URL}/app/worktickets/details/${futureTicket.workTicketId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'inherit' }}
                              >
                                WT#{futureTicket.workTicketId}
                              </Link>
                              {' — Next: '}
                              <Link
                                href={`${process.env.REACT_APP_ASPIRE_BASE_URL}/app/worktickets/details/${futureTicket.workTicketId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'inherit' }}
                              >
                                {formatDate(futureTicket.scheduledDate)}
                              </Link>
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Override scheduled date">
                            <IconButton size="small" onClick={() => openOverrideDialog(item)}>
                              <EventIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Mark as complete">
                            <IconButton size="small" onClick={() => openCompleteDialog(item)}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={item.ignored ? 'Unignore' : 'Ignore site'}>
                            <IconButton size="small" onClick={() => openIgnoreDialog(item)}>
                              {item.ignored ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onChangePage={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </TableContainer>

      {/* Override Dialog */}
      <Dialog open={overrideDialogOpen} onClose={() => setOverrideDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Override Scheduled Date</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Site: <strong>{selectedItem?.siteName}</strong>
          </Typography>
          <KeyboardDatePicker
            label="New Scheduled Date"
            format="MM/dd/yyyy"
            value={overrideDate}
            onChange={(date) => setOverrideDate(date)}
            fullWidth
            margin="normal"
            inputVariant="outlined"
          />
          <TextField
            label="Reason (optional)"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleOverrideSubmit} color="primary" variant="contained">Save Override</Button>
        </DialogActions>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={completeDialogOpen} onClose={() => setCompleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Application Complete</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Site: <strong>{selectedItem?.siteName}</strong>
          </Typography>
          <KeyboardDatePicker
            label="Completion Date"
            format="MM/dd/yyyy"
            value={completeDate}
            onChange={(date) => setCompleteDate(date)}
            fullWidth
            margin="normal"
            inputVariant="outlined"
          />
          <TextField
            label="Notes (optional)"
            value={completeNotes}
            onChange={(e) => setCompleteNotes(e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCompleteSubmit} color="primary" variant="contained">Mark Complete</Button>
        </DialogActions>
      </Dialog>

      {/* Ignore Confirmation Dialog */}
      <Dialog open={ignoreDialogOpen} onClose={() => setIgnoreDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedItem?.ignored ? 'Unignore Site' : 'Ignore Site'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Site: <strong>{selectedItem?.siteName}</strong>
          </Typography>
          {selectedItem?.ignored ? (
            <Typography variant="body2" color="textSecondary">
              This will restore this site to the active schedule. Are you sure?
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                This site will be hidden from the active schedule and marked as ignored.
              </Typography>
              <TextField
                label="Reason (optional)"
                value={ignoreReason}
                onChange={(e) => setIgnoreReason(e.target.value)}
                fullWidth
                margin="normal"
                variant="outlined"
                multiline
                rows={2}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIgnoreDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleIgnoreSubmit}
            color={selectedItem?.ignored ? 'primary' : 'secondary'}
            variant="contained"
          >
            {selectedItem?.ignored ? 'Unignore' : 'Ignore'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(HerbicideScheduleDashboard)
